export interface StringParamParser<T> {
    stringify(value: T): string | undefined;
    parse(query: string): T | undefined;
}

const stringParser: StringParamParser<string> = {
    stringify(value) {
        return value || undefined;
    },
    parse(query) {
        return query || undefined;
    },
};

const stringSetParser = <T extends string>(values: (string | T)[]): StringParamParser<T> => ({
    stringify(value) {
        return value || undefined;
    },
    parse(query) {
        if (query && values.includes(query as T)) {
            return query as T;
        }

        return undefined;
    },
});

const stringEnumParser = <TS extends string, TV>(map: Record<TS, TV>): StringParamParser<TV> => ({
    stringify(value) {
        for (const key of Object.keys(map) as TS[]) {
            if (map[key] === value) {
                return key;
            }
        }

        return undefined;
    },
    parse(query) {
        const key = query as TS;

        // eslint-disable-next-line no-prototype-builtins
        if (map.hasOwnProperty(key)) {
            return map[key];
        }

        return undefined;
    },
});

const uriParser: StringParamParser<string> = {
    stringify(value) {
        return value || undefined;
    },
    parse(query) {
        return query ? decodeURIComponent(query) : undefined;
    },
};

const numberParser: StringParamParser<number> = {
    stringify(value) {
        return `${+value}`;
    },
    parse(query) {
        const val = +query;

        return isNaN(val) ? undefined : val;
    },
};

const dateParser: StringParamParser<Date> = {
    stringify(value) {
        return `${value.getFullYear()}-${(value.getMonth() + 1).toString().padStart(2, '0')}-${value
            .getDate()
            .toString()
            .padStart(2, '0')}`;
    },
    parse(query) {
        const val = new Date(query + ' 00:00:00');

        return val.getTime() ? val : undefined;
    },
};

const boolParser: StringParamParser<boolean> = {
    stringify(value) {
        return value ? '1' : undefined;
    },
    parse(query) {
        return query === '1';
    },
};

export const paramParsers = {
    string: stringParser,
    stringSet: stringSetParser,
    stringEnum: stringEnumParser,
    uri: uriParser,
    number: numberParser,
    date: dateParser,
    bool: boolParser,
};
